import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Stethoscope, Pill, FlaskConical, Scale, HeartPulse, CigaretteOff,
  MapPin, DollarSign, Clock, ShieldCheck, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { telehealthServices } from '../data/telehealthServices';
import './ServicesPage.css';

const iconComponents = {
  stethoscope: Stethoscope,
  pill: Pill,
  flask: FlaskConical,
  scale: Scale,
  heartpulse: HeartPulse,
  cigaretteoff: CigaretteOff,
};

const serviceColors = ['#1565a8', '#f47920', '#cc1f00', '#1565a8', '#f47920', '#cc1f00'];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] },
});

const WaveDown = ({ fill = '#f5f7fb' }) => (
  <div className="sp-hero-wave">
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill={fill} />
    </svg>
  </div>
);

const pillars = [
  {
    icon: DollarSign,
    title: 'Transparent pricing',
    body: 'Every visit has a clear, upfront fee—no surprise bills. What you see is what you pay for our telehealth service.',
  },
  {
    icon: MapPin,
    title: 'Care where you are',
    body: 'Connect by video, phone, or message from home, work, or anywhere. No waiting rooms, no commute.',
  },
  {
    icon: Clock,
    title: 'On your schedule',
    body: 'Book when it works for you. Reach out by message 24/7 and we will get back to you as quickly as possible.',
  },
  {
    icon: ShieldCheck,
    title: 'Clinically sound',
    body: 'Our providers focus on evidence-based care—treating what we can safely treat virtually and referring when needed.',
  },
];

const steps = [
  { number: '01', title: 'Choose a service', body: 'Browse our six service lines and pick the visit type that fits your need.' },
  { number: '02', title: 'Book your visit', body: 'Schedule online or reach out by call, text, or email to set up an appointment.' },
  { number: '03', title: 'Connect from anywhere', body: 'Join your visit by video or phone. Get a care plan, prescription, or lab order—all virtually.' },
];

const ServicesPage = () => {
  return (
    <div className="sp-page">

      {/* ── Hero ── */}
      <section className="sp-hero">
        <div className="sp-hero-bg" />
        <div className="container sp-hero-inner">
          <motion.p className="sp-hero-eyebrow" {...fadeUp(0)}>
            Open Arms Healthcare Services LLC
          </motion.p>
          <motion.h1 className="sp-hero-headline" {...fadeUp(0.1)}>
            Expert telehealth care,<br /><em>wherever you are</em>
          </motion.h1>
          <motion.p className="sp-hero-sub" {...fadeUp(0.2)}>
            Safe, fast, and affordable virtual care with upfront pricing—by video, phone, or message.
            No waiting rooms. No hidden fees.
          </motion.p>
          <motion.div className="sp-hero-actions" {...fadeUp(0.3)}>
            <Link to="/contact" className="btn btn-primary sp-btn-lg">Book a visit</Link>
            <a href="#services" className="btn btn-outline-white sp-btn-lg">See all services</a>
          </motion.div>
          <motion.div className="sp-stats-bar" {...fadeUp(0.4)}>
            {[
              { value: '$25', label: 'Starting visit fee' },
              { value: '6', label: 'Service lines' },
              { value: '100%', label: 'Telehealth-first' },
              { value: '24/7', label: 'Message us anytime' },
            ].map((s) => (
              <div key={s.label} className="sp-stat">
                <span className="sp-stat-value">{s.value}</span>
                <span className="sp-stat-label">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
        <WaveDown fill="#f5f7fb" />
      </section>

      {/* ── Services Grid ── */}
      <section className="sp-services" id="services">
        <div className="container">
          <motion.div className="sp-section-header" {...fadeUp()}>
            <h2 className="sp-section-title">What we treat</h2>
            <p className="sp-section-sub">
              Six focused service lines designed to handle the most common virtual care needs—all with clear, per-visit fees.
            </p>
          </motion.div>
          <div className="sp-grid">
            {telehealthServices.map((svc, i) => {
              const Icon = iconComponents[svc.icon];
              const color = serviceColors[i];
              return (
                <motion.div
                  key={svc.slug}
                  className="sp-card"
                  {...fadeUp(i * 0.07)}
                  whileHover={{ y: -6 }}
                >
                  <div className="sp-card-icon" style={{ background: `${color}15`, color }}>
                    {Icon && <Icon size={28} />}
                  </div>
                  <div className="sp-card-price" style={{ color }}>
                    {svc.fee} <span>{svc.feeDetail}</span>
                  </div>
                  <h3 className="sp-card-title">{svc.title}</h3>
                  <p className="sp-card-summary">{svc.summary}</p>
                  <Link to={`/services/${svc.slug}`} className="sp-card-link" style={{ color }}>
                    Learn more <ArrowRight size={15} />
                  </Link>
                  <div className="sp-card-bar" style={{ background: color }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Why Open Arms ── */}
      <section className="sp-pillars">
        <div className="container">
          <motion.div className="sp-section-header" {...fadeUp()}>
            <h2 className="sp-section-title">Why Open Arms</h2>
            <p className="sp-section-sub">
              We built our practice around one idea: quality healthcare should be easy to access for everyone.
            </p>
          </motion.div>
          <div className="sp-pillars-grid">
            {pillars.map((p, i) => (
              <motion.div key={p.title} className="sp-pillar" {...fadeUp(i * 0.1)}>
                <div className="sp-pillar-icon">
                  <p.icon size={26} />
                </div>
                <h4 className="sp-pillar-title">{p.title}</h4>
                <p className="sp-pillar-body">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="sp-how">
        <div className="container">
          <motion.div className="sp-section-header" {...fadeUp()}>
            <h2 className="sp-section-title">How it works</h2>
            <p className="sp-section-sub">Getting care takes three simple steps.</p>
          </motion.div>
          <div className="sp-steps">
            {steps.map((step, i) => (
              <motion.div key={step.number} className="sp-step" {...fadeUp(i * 0.12)}>
                <div className="sp-step-number">{step.number}</div>
                <h4 className="sp-step-title">{step.title}</h4>
                <p className="sp-step-body">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Note ── */}
      <section className="sp-pricing-note">
        <div className="container">
          <motion.div className="sp-note-box" {...fadeUp()}>
            <h3 className="sp-note-title">About our pricing</h3>
            <div className="sp-note-list">
              {[
                'All fees listed are for our telehealth visit only.',
                'Medication costs are billed separately by your pharmacy.',
                'Lab fees are charged by the lab or facility—not by us.',
                'No insurance required. We accept self-pay patients.',
              ].map((item) => (
                <div key={item} className="sp-note-item">
                  <CheckCircle2 size={18} className="sp-note-check" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="sp-cta">
        <div className="container sp-cta-inner">
          <motion.h2 className="sp-cta-headline" {...fadeUp()}>
            Ready to get started?
          </motion.h2>
          <motion.p className="sp-cta-sub" {...fadeUp(0.1)}>
            Book a visit today—no referral needed, no waiting room required.
          </motion.p>
          <motion.div {...fadeUp(0.2)}>
            <Link to="/contact" className="btn btn-primary sp-btn-lg">Book a visit now</Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default ServicesPage;
